import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarClock,
  Hourglass,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Plus,
  SkipForward,
  Trash2,
} from "lucide-react";

import {
  APIError,
  cancelSchedule,
  createSchedule,
  finishAfterNextSchedule,
  listChannels,
  listSchedules,
  pauseSchedule,
  resumeSchedule,
  skipNextSchedule,
  updateSchedule,
  type Channel,
  type Schedule,
  type ScheduleCreateInput,
  type ScheduleStatus,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Page";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { cn } from "@/lib/utils";

function errMsg(e: unknown, fallback: string) {
  return humanizeError(e, fallback);
}
const is403 = (e: unknown) => e instanceof APIError && e.status === 403;

// schedErr surfaces the server's specific schedule validation sentence (e.g.
// "max_runs … already run N times", "pick an agent-role channel") — these are
// user-safe, crafted messages — while keeping the friendly generic handling for
// auth/5xx errors. humanizeError otherwise collapses every 400 to a generic line.
function schedErr(e: unknown, fallback: string): string {
  if (e instanceof APIError && e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 403 && e.message) {
    return e.message;
  }
  return humanizeError(e, fallback);
}

const STATUS_CLS: Record<ScheduleStatus, string> = {
  scheduled: "border-success/30 bg-success/10 text-success",
  firing: "border-primary/30 bg-primary/10 text-primary",
  paused: "border-warning/40 bg-warning/10 text-warning",
  done: "border-border bg-muted text-muted-foreground",
  cancelled: "border-border bg-muted text-muted-foreground",
  failed: "border-destructive/40 bg-destructive/10 text-destructive",
};

const CHANNEL_LABEL: Record<string, string> = {
  dashboard: "Dashboard chat",
  telegram: "Telegram chat",
  slack: "Slack chat",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// humanizeCron renders the common daily / weekly cron shapes readably and falls
// back to the raw expression for anything else.
function humanizeCron(cron?: string): string {
  if (!cron) return "Recurring";
  const f = cron.trim().split(/\s+/);
  if (f.length !== 5) return `Cron: ${cron}`;
  const [min, hour, dom, mon, dow] = f;
  const isNum = (s: string) => /^\d+$/.test(s);
  if (isNum(min) && isNum(hour) && dom === "*" && mon === "*") {
    const hhmm = `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;
    if (dow === "*") return `Daily at ${hhmm}`;
    if (isNum(dow) && Number(dow) <= 6) return `Weekly on ${WEEKDAYS[Number(dow)]} at ${hhmm}`;
  }
  return `Cron: ${cron}`;
}

function scheduleSummary(s: Schedule): string {
  return s.recurrence === "cron" ? humanizeCron(s.cron) : "One-off";
}

// fmtNextRun renders fire_at in the task's own timezone (so it matches the
// wall-clock the user asked for, not UTC/their browser).
function fmtNextRun(s: Schedule): string {
  const d = new Date(s.fire_at);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString(undefined, {
      timeZone: s.timezone || undefined,
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return d.toLocaleString();
  }
}

function boundsLabel(s: Schedule): string {
  const parts: string[] = [];
  if (s.max_runs && s.max_runs > 0) parts.push(`${s.runs_count}/${s.max_runs} runs`);
  if (s.until) parts.push(`until ${new Date(s.until).toLocaleDateString()}`);
  return parts.join(" · ");
}

function createdByLabel(by: string): string {
  if (by.startsWith("agent:")) return "Agent";
  if (by === "api-key") return "API";
  // A human-created schedule stores a raw user id; we don't resolve names here.
  return "A teammate";
}

function isActive(s: Schedule): boolean {
  return s.status === "scheduled" || s.status === "paused" || s.status === "firing";
}

export function ScheduledTab({ projectId }: { projectId: string }) {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // null = closed; "new" = create; a Schedule = edit that row.
  const [dialog, setDialog] = useState<Schedule | "new" | null>(null);
  const toast = useToast();
  const confirm = useConfirm();

  const refresh = useCallback(async () => {
    try {
      const [items, chans] = await Promise.all([
        listSchedules(projectId),
        // Channels are admin-gated; an editor may 403 — degrade to "no channels".
        listChannels(projectId)
          .then((r) => r.channels)
          .catch(() => [] as Channel[]),
      ]);
      setSchedules(items);
      setChannels(chans.filter((c) => c.role === "agent" && c.enabled));
      setErr(null);
    } catch (e) {
      setErr(errMsg(e, "Couldn't load schedules"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const apply = useCallback((updated: Schedule) => {
    setSchedules((cur) => cur.map((s) => (s.id === updated.id ? updated : s)));
  }, []);

  const onPause = useCallback(
    async (s: Schedule) => {
      try {
        apply(await pauseSchedule(projectId, s.id));
        toast.success("Schedule paused.");
      } catch (e) {
        toast.error(is403(e) ? "You need editor access to manage schedules." : schedErr(e, "Couldn't pause"));
      }
    },
    [projectId, apply, toast],
  );

  const onResume = useCallback(
    async (s: Schedule) => {
      try {
        apply(await resumeSchedule(projectId, s.id));
        toast.success("Schedule resumed.");
      } catch (e) {
        toast.error(is403(e) ? "You need editor access to manage schedules." : schedErr(e, "Couldn't resume"));
      }
    },
    [projectId, apply, toast],
  );

  const onSkipNext = useCallback(
    async (s: Schedule) => {
      try {
        const updated = await skipNextSchedule(projectId, s.id);
        apply(updated);
        toast.success(`Skipped — next run ${fmtNextRun(updated)}.`);
      } catch (e) {
        toast.error(is403(e) ? "You need editor access to manage schedules." : schedErr(e, "Couldn't skip"));
      }
    },
    [projectId, apply, toast],
  );

  const onFinishAfterNext = useCallback(
    async (s: Schedule) => {
      const ok = await confirm({
        title: "Stop after the next run?",
        description: "It runs one more time, then ends. The remaining recurrence is cancelled.",
        confirmText: "Stop after next",
      });
      if (!ok) return;
      try {
        apply(await finishAfterNextSchedule(projectId, s.id));
        toast.success("It'll run once more, then stop.");
      } catch (e) {
        toast.error(is403(e) ? "You need editor access to manage schedules." : schedErr(e, "Couldn't update"));
      }
    },
    [projectId, apply, confirm, toast],
  );

  const onCancel = useCallback(
    async (s: Schedule) => {
      const ok = await confirm({
        title: "Cancel this schedule?",
        description:
          s.recurrence === "cron"
            ? "It stops recurring. This can't be undone."
            : "It won't run. This can't be undone.",
        confirmText: "Cancel schedule",
        destructive: true,
      });
      if (!ok) return;
      try {
        await cancelSchedule(projectId, s.id);
        // Reflect the terminal state locally (the row lingers ~7 days for history).
        setSchedules((cur) => cur.map((x) => (x.id === s.id ? { ...x, status: "cancelled" as ScheduleStatus } : x)));
        toast.success("Schedule cancelled.");
      } catch (e) {
        toast.error(is403(e) ? "You need editor access to manage schedules." : schedErr(e, "Couldn't cancel"));
      }
    },
    [projectId, confirm, toast],
  );

  const { active, history } = useMemo(() => {
    const a: Schedule[] = [];
    const h: Schedule[] = [];
    for (const s of schedules) (isActive(s) ? a : h).push(s);
    return { active: a, history: h };
  }, [schedules]);

  const rowProps = { onPause, onResume, onSkipNext, onFinishAfterNext, onEdit: (s: Schedule) => setDialog(s), onCancel };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Scheduled runs</CardTitle>
          <CardDescription>
            One-off and recurring tasks this agent runs on a timer — created here or by asking the agent (e.g.{" "}
            <span className="italic">"every morning at 7 send me yesterday's stats"</span>). Results are delivered to the
            channel you choose.
          </CardDescription>
        </div>
        <Button
          size="sm"
          onClick={() => setDialog("new")}
          disabled={channels.length === 0}
          title={channels.length === 0 ? "Connect a Telegram or Slack channel (agent role) in Channels first" : undefined}
        >
          <Plus className="h-4 w-4" /> New schedule
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : err ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="text-destructive">{err}</span>
            <Button variant="outline" size="sm" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        ) : schedules.length === 0 ? (
          <div className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
            <CalendarClock className="h-6 w-6 opacity-50" />
            <span>
              No scheduled runs yet.{" "}
              {channels.length === 0 ? (
                <>
                  Connect a <span className="font-medium text-foreground">Telegram or Slack channel</span> in
                  Channels, then schedule a run.
                </>
              ) : (
                <>
                  <span className="font-medium text-foreground">New schedule</span>, or ask the agent to schedule
                  something.
                </>
              )}
            </span>
          </div>
        ) : (
          <>
            <ScheduleList schedules={active} {...rowProps} />
            {history.length > 0 && (
              <div className="space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">History</div>
                <ScheduleList schedules={history} {...rowProps} />
              </div>
            )}
          </>
        )}
      </CardContent>

      {dialog && (
        <ScheduleDialog
          projectId={projectId}
          schedule={dialog === "new" ? null : dialog}
          channels={channels}
          onClose={() => setDialog(null)}
          onSaved={(s, created) => {
            if (created) setSchedules((cur) => [s, ...cur]);
            else apply(s);
            setDialog(null);
          }}
        />
      )}
    </Card>
  );
}

type RowActions = {
  onPause: (s: Schedule) => void;
  onResume: (s: Schedule) => void;
  onSkipNext: (s: Schedule) => void;
  onFinishAfterNext: (s: Schedule) => void;
  onEdit: (s: Schedule) => void;
  onCancel: (s: Schedule) => void;
};

function ScheduleList({ schedules, ...a }: { schedules: Schedule[] } & RowActions) {
  return (
    <ul className="divide-y rounded-md border">
      {schedules.map((s) => {
        const editable = s.status === "scheduled" || s.status === "paused";
        const cancellable = s.status === "scheduled" || s.status === "paused" || s.status === "failed";
        const isCron = s.recurrence === "cron";
        const hasActions = editable || cancellable;
        const bounds = boundsLabel(s);
        return (
          <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <Badge variant="outline" className={cn("shrink-0 text-[11px] capitalize", STATUS_CLS[s.status])}>
                {s.status}
              </Badge>
              <div className="min-w-0">
                <div className="truncate text-sm">{s.payload}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {scheduleSummary(s)} · next {fmtNextRun(s)} · {CHANNEL_LABEL[s.channel_kind] ?? s.channel_kind} · by{" "}
                  {createdByLabel(s.created_by)}
                  {bounds && <span> · {bounds}</span>}
                  {s.last_error && <span className="text-destructive"> · last error: {s.last_error}</span>}
                </div>
              </div>
            </div>
            {hasActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Schedule actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {s.status === "scheduled" && (
                    <DropdownMenuItem onSelect={() => a.onPause(s)}>
                      <Pause /> Pause
                    </DropdownMenuItem>
                  )}
                  {s.status === "paused" && (
                    <DropdownMenuItem onSelect={() => a.onResume(s)}>
                      <Play /> Resume
                    </DropdownMenuItem>
                  )}
                  {s.status === "scheduled" && isCron && (
                    <DropdownMenuItem onSelect={() => a.onSkipNext(s)}>
                      <SkipForward /> Skip next run
                    </DropdownMenuItem>
                  )}
                  {s.status === "scheduled" && isCron && (
                    <DropdownMenuItem onSelect={() => a.onFinishAfterNext(s)}>
                      <Hourglass /> Stop after next run
                    </DropdownMenuItem>
                  )}
                  {editable && (
                    <DropdownMenuItem onSelect={() => a.onEdit(s)}>
                      <Pencil /> Edit
                    </DropdownMenuItem>
                  )}
                  {cancellable && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => a.onCancel(s)} className="text-destructive focus:text-destructive">
                        <Trash2 /> Cancel
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </li>
        );
      })}
    </ul>
  );
}

type EditKind = "once" | "daily" | "weekly" | "cron";

function hhmmFromCron(cron?: string): string {
  if (!cron) return "";
  const f = cron.trim().split(/\s+/);
  if (f.length !== 5 || !/^\d+$/.test(f[0]) || !/^\d+$/.test(f[1])) return "";
  return `${f[1].padStart(2, "0")}:${f[0].padStart(2, "0")}`;
}

function initialKind(s: Schedule | null): EditKind {
  if (!s) return "daily";
  if (s.recurrence === "once") return "once";
  const f = (s.cron ?? "").trim().split(/\s+/);
  if (f.length === 5 && /^\d+$/.test(f[4]) && Number(f[4]) <= 6) return "weekly";
  if (f.length === 5 && f[4] === "*" && /^\d+$/.test(f[0]) && /^\d+$/.test(f[1])) return "daily";
  return "cron";
}

// ScheduleDialog creates a new schedule (with a "deliver to" channel picker) or
// edits an existing one's payload, timing and end-bounds. The delivery target is
// fixed once created (recreate to retarget).
function ScheduleDialog({
  projectId,
  schedule,
  channels,
  onClose,
  onSaved,
}: {
  projectId: string;
  schedule: Schedule | null;
  channels: Channel[];
  onClose: () => void;
  onSaved: (s: Schedule, created: boolean) => void;
}) {
  const creating = schedule === null;
  const [channelId, setChannelId] = useState(channels[0]?.id ?? "");
  const [payload, setPayload] = useState(schedule?.payload ?? "");
  const [kind, setKind] = useState<EditKind>(initialKind(schedule));
  const [time, setTime] = useState(() => hhmmFromCron(schedule?.cron) || "09:00");
  const [weekday, setWeekday] = useState(() => {
    const f = (schedule?.cron ?? "").trim().split(/\s+/);
    return f.length === 5 && /^\d+$/.test(f[4]) ? Number(f[4]) : 1;
  });
  const [cron, setCron] = useState(schedule?.cron ?? "0 9 * * *");
  const [onceAt, setOnceAt] = useState(() => {
    if (!schedule || schedule.recurrence !== "once") return "";
    const d = new Date(schedule.fire_at);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [timezone, setTimezone] = useState(
    schedule?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  const [maxRuns, setMaxRuns] = useState(schedule?.max_runs ? String(schedule.max_runs) : "");
  const [until, setUntil] = useState(schedule?.until ? schedule.until.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const onSave = useCallback(async () => {
    if (!payload.trim()) {
      toast.error("Instruction can't be empty.");
      return;
    }
    if (creating && !channelId) {
      toast.error("Pick a channel to deliver to.");
      return;
    }
    // Timing fields, shared by create + edit (no payload/channel/bounds — those
    // are set explicitly, so spreading this can't clobber them).
    type Timing = Pick<
      ScheduleCreateInput,
      "recurrence" | "fire_at" | "cron" | "timezone" | "preset" | "preset_time" | "preset_weekday"
    >;
    const timing: Timing = { recurrence: kind === "once" ? "once" : "cron" };
    if (kind === "once") {
      if (!onceAt) {
        toast.error("Pick a date and time.");
        return;
      }
      timing.fire_at = new Date(onceAt).toISOString();
    } else if (kind === "cron") {
      timing.cron = cron.trim();
      timing.timezone = timezone;
    } else {
      timing.preset = kind; // daily | weekly
      timing.preset_time = time;
      timing.timezone = timezone;
      if (kind === "weekly") timing.preset_weekday = weekday;
    }
    // Bounds (cron only — a one-shot has no recurrence to bound).
    const bounds: { until: string; max_runs: number } = {
      // Local end-of-day (no "Z"): the date field is wall-clock, and the schedule
      // tz defaults to the browser's, so this lands on the chosen day's evening.
      until: kind !== "once" && until ? new Date(until + "T23:59:59").toISOString() : "",
      max_runs: kind !== "once" && maxRuns.trim() ? Number(maxRuns) : 0,
    };
    setSaving(true);
    try {
      if (creating) {
        const created = await createSchedule(projectId, {
          channel_id: channelId,
          payload: payload.trim(),
          ...timing,
          ...bounds,
        });
        onSaved(created, true);
        toast.success("Schedule created.");
      } else {
        const updated = await updateSchedule(projectId, schedule!.id, {
          payload: payload.trim(),
          ...timing,
          ...bounds,
        });
        onSaved(updated, false);
        toast.success("Schedule updated.");
      }
    } catch (e) {
      toast.error(is403(e) ? "You need editor access." : schedErr(e, "Couldn't save"));
    } finally {
      setSaving(false);
    }
  }, [creating, channelId, payload, kind, onceAt, cron, time, weekday, timezone, maxRuns, until, projectId, schedule, onSaved, toast]);

  const inputCls =
    "w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{creating ? "New schedule" : "Edit schedule"}</DialogTitle>
          <DialogDescription>
            {creating
              ? "Schedule a one-off or recurring run. The result is delivered to the channel you pick."
              : `Change what runs and when. Delivery stays in ${CHANNEL_LABEL[schedule!.channel_kind] ?? schedule!.channel_kind}.`}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto px-0.5">
          {creating && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Deliver to</label>
              <select value={channelId} onChange={(e) => setChannelId(e.target.value)} className={inputCls}>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({CHANNEL_LABEL[c.kind] ?? c.kind})
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">The agent messages you here when the schedule fires.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Instruction</label>
            <textarea
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              rows={3}
              className={cn(inputCls, "resize-y")}
              placeholder="What the agent should do when this fires"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Repeat</label>
            <select value={kind} onChange={(e) => setKind(e.target.value as EditKind)} className={inputCls}>
              <option value="once">Once</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="cron">Custom (cron)</option>
            </select>
          </div>

          {kind === "once" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">When</label>
              <input type="datetime-local" value={onceAt} onChange={(e) => setOnceAt(e.target.value)} className={inputCls} />
              <p className="text-xs text-muted-foreground">In your local time.</p>
            </div>
          )}

          {(kind === "daily" || kind === "weekly") && (
            <div className="grid grid-cols-2 gap-3">
              {kind === "weekly" && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Day</label>
                  <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} className={inputCls}>
                    {WEEKDAYS.map((d, i) => (
                      <option key={i} value={i}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Time</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          {kind === "cron" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Cron expression</label>
              <input
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                placeholder="0 9 * * *"
                className={cn(inputCls, "font-mono")}
              />
              <p className="text-xs text-muted-foreground">5-field (min hour dom mon dow). Minimum 5 minutes between runs.</p>
            </div>
          )}

          {kind !== "once" && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Timezone</label>
                <input
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  placeholder="Europe/Berlin"
                  className={inputCls}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Stop after</label>
                  <input
                    type="number"
                    min={0}
                    value={maxRuns}
                    onChange={(e) => setMaxRuns(e.target.value)}
                    placeholder="runs (blank = ∞)"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">End date</label>
                  <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className={inputCls} />
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void onSave()} disabled={saving}>
            {saving ? "Saving…" : creating ? "Create schedule" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
